import PSTableDataSource from './PSTableDataSource.js';

/**
 * Data source for handling data retrieval via a callback function in PS Table.
 *
 * PSTableDataSourceCallback delegates data retrieval to a custom callback function,
 * passing the paging, sorting, and filtering instances as parameters. This allows
 * for custom data fetching logic while maintaining the standard PS Table interface.
 *
 * @class PSTableDataSourceCallback
 * @extends PSTableDataSource
 * @example
 * // Use with a custom callback function
 * const dataSource = new PSTableDataSourceCallback({
 *   data_source: async (params) => {
 *     const { paging, sorting, filtering } = params;
 *     // Custom data fetching logic
 *     return await fetchCustomData(paging, sorting, filtering);
 *   },
 *   paging: pagingInstance,
 *   sorting: sortingInstance,
 *   filtering: filteringInstance
 * });
 */
class PSTableDataSourceCallback extends PSTableDataSource {
	/**
	 * Creates a new PSTableDataSourceCallback instance.
	 *
	 * @param {Object} options - Data source configuration options
	 * @param {Function} options.data_source - Callback function for data retrieval
	 * @param {PSTablePaging} options.paging - The pagination instance for the ps-table component
	 * @param {PSTableSorting} options.sorting - The sorting instance for the ps-table component
	 * @param {PSTableFiltering} options.filtering - The filtering instance for the ps-table component
	 */
	constructor(options) {
		super(options);
		this.mode = 'Function callback';
	}

	/**
	 * Retrieves data by calling the custom callback function.
	 *
	 * Passes the paging, sorting, and filtering instances to the callback
	 * function, allowing for custom data fetching logic while maintaining
	 * the standard PS Table interface.
	 *
	 * @returns {Promise<Array<Object>>} A promise that resolves to the data returned by the callback
	 */
	async select() {
		return await this.data_source({
			paging: this.paging,
			sorting: this.sorting,
			filtering: this.filtering
		});
	}
}

export default PSTableDataSourceCallback;
